import { type ReactNode, useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';

import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import {
  createEventRequest,
  fetchAccountEvents,
  fetchEvents,
  GooglePlacesAutocomplete,
  readCachedAccountEvents,
  readCachedEvents,
  type EventSummary,
} from 'event';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import Footer from 'examples/Footer';
import { normalizeEndDateTimeForStartChange } from 'lib/datetime-local';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

interface EventRow {
  title: string;
  location: ReactNode;
  description: string;
  startsAt: string;
  endsAt: string;
  detailsPath: string;
}

function formatEventDateTime(value: string, unknownLabel: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return unknownLabel;
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toISOString();
  }
}

function toDateTimeLocalValue(value: Date): string {
  const localValue = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return localValue.toISOString().slice(0, 16);
}

function getDefaultEventTimeRange() {
  const startsAt = new Date();
  const endsAt = new Date(startsAt.getTime() + 60 * 60 * 1000);

  return {
    startsAt: toDateTimeLocalValue(startsAt),
    endsAt: toDateTimeLocalValue(endsAt),
  };
}

function extractRegionFromCityLabel(cityLabel: string): string {
  const parts = cityLabel
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return '';
  }

  return parts.slice(1).join(', ');
}

function renderCityLocationValue(
  event: EventSummary,
  translations: ReturnType<typeof getTranslations>['eventsPage']
): ReactNode {
  const cityValue = event.city?.label.trim() || translations.notSpecified;
  const locationValue = event.location?.trim() || translations.notSpecified;

  return (
    <SoftBox display="flex" flexDirection="column" lineHeight={1.25}>
      <SoftTypography variant="caption" color="dark" fontWeight="medium">
        {translations.cityLabel}: {cityValue}
      </SoftTypography>
      <SoftTypography variant="caption" color="text" fontWeight="regular">
        {translations.locationLabel}: {locationValue}
      </SoftTypography>
    </SoftBox>
  );
}

function toTableRows(
  events: EventSummary[],
  translations: ReturnType<typeof getTranslations>['eventsPage']
): EventRow[] {
  return events.map((event) => ({
    title: event.title,
    location: renderCityLocationValue(event, translations),
    description: event.description?.trim() || translations.noDescription,
    startsAt: formatEventDateTime(event.startsAt, translations.unknownDate),
    endsAt: formatEventDateTime(event.endsAt, translations.unknownDate),
    detailsPath: `/event/${event.uuid}`,
  }));
}

function Events() {
  const { account, accessToken } = useAuth();
  const navigate = useNavigate();
  const locale = useLocale();
  const translations = getTranslations(locale).eventsPage;
  const cachedEvents = account?.uuid
    ? readCachedAccountEvents(account.uuid)
    : readCachedEvents();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [cityPlaceId, setCityPlaceId] = useState<string | null>(null);
  const [cityCoordinates, setCityCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [location, setLocation] = useState('');
  const [startsAtInput, setStartsAtInput] = useState(() => getDefaultEventTimeRange().startsAt);
  const [endsAtInput, setEndsAtInput] = useState(() => getDefaultEventTimeRange().endsAt);
  const [events, setEvents] = useState<EventSummary[]>(() => cachedEvents ?? []);
  const [isLoading, setIsLoading] = useState(() => cachedEvents === undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const hasSelectedCity = city.trim().length > 0;
  const selectedRegion = extractRegionFromCityLabel(city);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (submitErrorMessage) {
      showSuiErrorSnackbar(submitErrorMessage);
    }
  }, [submitErrorMessage]);

  const loadAccountEvents = async (
    accountUuid: string,
    token?: string
  ): Promise<EventSummary[]> => fetchAccountEvents(accountUuid, token);

  const reloadActiveAccountEvents = async (accountUuid: string) => {
    const accountEvents = await loadAccountEvents(accountUuid, accessToken ?? undefined);
    setEvents(accountEvents);
  };

  useEffect(() => {
    let isCancelled = false;

    async function loadEvents() {
      const cachedData = account?.uuid
        ? readCachedAccountEvents(account.uuid)
        : readCachedEvents();

      if (!isCancelled && cachedData !== undefined) {
        setEvents(cachedData);
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');
      setSubmitErrorMessage('');

      try {
        if (account?.uuid) {
          const accountEvents = await loadAccountEvents(
            account.uuid,
            accessToken ?? undefined
          );

          if (!isCancelled) {
            setEvents(accountEvents);
          }

          return;
        }

        const allEvents = await fetchEvents(accessToken ?? undefined);
        if (!isCancelled) {
          setEvents(allEvents);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : translations.loadFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadEvents();

    return () => {
      isCancelled = true;
    };
  }, [accessToken, account?.uuid, translations.loadFailed]);

  const handleOpenCreateDialog = () => {
    const defaultRange = getDefaultEventTimeRange();
    setTitle('');
    setDescription('');
    setCity('');
    setCityPlaceId(null);
    setCityCoordinates(null);
    setLocation('');
    setStartsAtInput(defaultRange.startsAt);
    setEndsAtInput(defaultRange.endsAt);
    setSubmitErrorMessage('');
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (isSubmitting) {
      return;
    }

    setIsCreateDialogOpen(false);
  };

  const handleStartsAtInputChange = (nextValue: string) => {
    setStartsAtInput(nextValue);
    setEndsAtInput((currentEndsAtInput) =>
      normalizeEndDateTimeForStartChange(nextValue, currentEndsAtInput)
    );
  };

  const handleCityChange = (nextCity: string) => {
    setCity(nextCity);
    if (!nextCity.trim().length) {
      setCityPlaceId(null);
      setCityCoordinates(null);
      setLocation('');
    }
  };

  const handleCreateEvent = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const normalizedCity = city.trim();
    const normalizedLocation = location.trim();
    const shouldUseLocation = normalizedCity.length > 0;
    const startsAtDate = new Date(startsAtInput);
    const endsAtDate = new Date(endsAtInput);

    if (!account?.uuid) {
      setSubmitErrorMessage(translations.validationAccountMissing);
      return;
    }

    if (normalizedTitle.length < 2) {
      setSubmitErrorMessage(translations.validationTitleMin);
      return;
    }

    if (normalizedDescription.length > 2000) {
      setSubmitErrorMessage(translations.validationDescriptionMax);
      return;
    }

    if (normalizedCity.length > 512) {
      setSubmitErrorMessage(translations.validationCityMax);
      return;
    }

    if (shouldUseLocation && normalizedLocation.length > 255) {
      setSubmitErrorMessage(translations.validationLocationMax);
      return;
    }

    if (!startsAtInput || !Number.isFinite(startsAtDate.getTime())) {
      setSubmitErrorMessage(translations.validationStartInvalid);
      return;
    }

    if (!endsAtInput || !Number.isFinite(endsAtDate.getTime())) {
      setSubmitErrorMessage(translations.validationEndInvalid);
      return;
    }

    if (endsAtDate.getTime() <= startsAtDate.getTime()) {
      setSubmitErrorMessage(translations.validationEndAfterStart);
      return;
    }

    setSubmitErrorMessage('');
    setIsSubmitting(true);

    try {
      await createEventRequest(
        {
          accountUuid: account.uuid,
          title: normalizedTitle,
          description: normalizedDescription || undefined,
          city: normalizedCity
            ? {
                label: normalizedCity,
                placeId: cityPlaceId,
                coordinates: cityCoordinates,
              }
            : null,
          location: shouldUseLocation ? normalizedLocation || null : null,
          startsAt: startsAtDate.toISOString(),
          endsAt: endsAtDate.toISOString(),
        },
        accessToken ?? undefined
      );

      const defaultRange = getDefaultEventTimeRange();
      setTitle('');
      setDescription('');
      setCity('');
      setCityPlaceId(null);
      setCityCoordinates(null);
      setLocation('');
      setStartsAtInput(defaultRange.startsAt);
      setEndsAtInput(defaultRange.endsAt);
      setIsCreateDialogOpen(false);

      if (account?.uuid) {
        await reloadActiveAccountEvents(account.uuid);
      } else {
        const allEvents = await fetchEvents(accessToken ?? undefined);
        setEvents(allEvents);
      }
    } catch (error) {
      setSubmitErrorMessage(
        error instanceof Error ? error.message : translations.createFailed
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableData = useMemo(
    () => ({
      columns: [
        { Header: translations.tableEvent, accessor: 'title', width: '20%' },
        { Header: translations.tableCityLocation, accessor: 'location', width: '24%' },
        { Header: translations.tableStarts, accessor: 'startsAt', width: '18%' },
        { Header: translations.tableEnds, accessor: 'endsAt', width: '18%' },
        { Header: translations.tableDescription, accessor: 'description' },
      ],
      rows: toTableRows(events, translations),
    }),
    [events, translations]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  gap={2}
                  mb={!account?.uuid ? 2 : 0}
                >
                  <SoftBox lineHeight={1}>
                    <SoftTypography variant="h5" fontWeight="medium">
                      {translations.title}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      {translations.listDescription}
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    onClick={handleOpenCreateDialog}
                    disabled={!account?.uuid}
                  >
                    {translations.createAction}
                  </SoftButton>
                </SoftBox>
                {!account?.uuid ? (
                  <SoftTypography variant="button" color="text">
                    {translations.accountMissing}
                  </SoftTypography>
                ) : null}
              </SoftBox>
              {isLoading ? (
                <SoftBox px={3} pb={3}>
                  <SoftTypography variant="button" color="text">
                    {translations.loading}
                  </SoftTypography>
                </SoftBox>
              ) : tableData.rows.length === 0 ? (
                <SoftBox px={3} pb={3}>
                  <SoftTypography variant="button" color="text">
                    {translations.empty}
                  </SoftTypography>
                </SoftBox>
              ) : (
                <DataTable
                  table={tableData}
                  canSearch
                  onRowClick={(row: EventRow) => navigate(row.detailsPath)}
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />

      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{translations.createDialogTitle}</DialogTitle>
        <SoftBox component="form" onSubmit={handleCreateEvent}>
          <DialogContent>
            <Stack spacing={2}>
              <SoftInput
                placeholder={translations.titlePlaceholder}
                value={title}
                onChange={(inputEvent) => setTitle(inputEvent.target.value)}
              />
              <SoftInput
                placeholder={translations.descriptionPlaceholder}
                multiline
                rows={3}
                value={description}
                onChange={(inputEvent) => setDescription(inputEvent.target.value)}
              />
              <GooglePlacesAutocomplete
                placeholder={translations.cityPlaceholder}
                helperText={translations.cityHelper}
                value={city}
                onChange={handleCityChange}
                onPlaceSelect={(place) => {
                  setCityPlaceId(place?.placeId ?? null);
                  setCityCoordinates(place?.coordinates ?? null);
                }}
                disabled={isSubmitting}
                restrictToCities
              />
              {hasSelectedCity ? (
                <GooglePlacesAutocomplete
                  placeholder={translations.locationPlaceholder}
                  helperText={translations.locationHelper}
                  value={location}
                  onChange={setLocation}
                  disabled={isSubmitting}
                  restrictToEstablishments
                  regionContext={selectedRegion}
                />
              ) : (
                <SoftTypography variant="caption" color="text" fontWeight="regular">
                  {translations.selectCityFirst}
                </SoftTypography>
              )}
              <SoftBox
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                gap={2}
              >
                  <SoftInput
                    type="datetime-local"
                    value={startsAtInput}
                    onChange={(inputEvent) =>
                      handleStartsAtInputChange(inputEvent.target.value)
                    }
                  />
                <SoftInput
                  type="datetime-local"
                  value={endsAtInput}
                  onChange={(inputEvent) => setEndsAtInput(inputEvent.target.value)}
                />
              </SoftBox>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <SoftButton
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleCloseCreateDialog}
              disabled={isSubmitting}
            >
              {translations.cancel}
            </SoftButton>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={isSubmitting || !title.trim() || !startsAtInput || !endsAtInput}
            >
              {isSubmitting ? translations.createSubmitting : translations.createSubmit}
            </SoftButton>
          </DialogActions>
        </SoftBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default Events;
